<script setup>
import FileTextIcon from '@iconify-vue/ph/file-text';
import ListChecksIcon from '@iconify-vue/ph/list-checks-fill';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { theme } = defineProps({
    theme: {
        type: [String, Array],
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.fullName,
                    length: 240,
                }),
        }),
        columnHelper.display({
            id: 'details',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Détails',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            ...(props.row.original.description
                                ? [
                                      h(
                                          DisplayContentAction,
                                          {
                                              title: 'Description',
                                              content:
                                                  props.row.original
                                                      .description,
                                          },
                                          {
                                              trigger: () =>
                                                  h(ActionButton, {
                                                      size: 'small',
                                                      square: true,
                                                      type: 'primary',
                                                      title: 'Voir la description',
                                                      icon: FileTextIcon,
                                                  }),
                                          },
                                      ),
                                  ]
                                : []),
                            ...(props.row.original.requirements
                                ? [
                                      h(
                                          DisplayContentAction,
                                          {
                                              title: 'Besoins',
                                              content:
                                                  props.row.original
                                                      .requirements,
                                          },
                                          {
                                              trigger: () =>
                                                  h(ActionButton, {
                                                      size: 'small',
                                                      square: true,
                                                      title: 'Voir les besoins',
                                                      type: 'primary',
                                                      icon: ListChecksIcon,
                                                  }),
                                          },
                                      ),
                                  ]
                                : []),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'price',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Prix',
                }),
            cell: (props) =>
                props.row.original.price !== null
                    ? h(PriceCell, {
                          price: props.row.original.price,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'netlinkingBudgetPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Budget netlinking/mois',
                }),
            cell: (props) =>
                props.row.original.netlinkingBudgetPerMonth !== null
                    ? h(PriceCell, {
                          price: props.row.original.netlinkingBudgetPerMonth,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'hoursPerMonth',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Heures/mois',
                }),
            cell: (props) =>
                props.row.original.hoursPerMonth !== null
                    ? h(NumberCell, {
                          number: props.row.original.hoursPerMonth,
                          suffix: 'h',
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.apikom.package',
                                    {
                                        package: props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                                square: true,
                            }),
                        ],
                    },
                ),
        }),
    ],
    data: {
        theme,
    },
    columnVisibility: {
        price:
            theme === null ||
            [
                'seo',
                'sea',
                'smo',
                'sma',
                'sem',
                'dev',
                'hosting',
                'outsourcing',
                'cloudflare',
                'emailing',
                'shoppingfeed',
                'days',
                'special',
            ].includes(theme),
        netlinkingBudgetPerMonth: theme === 'seo',
        hoursPerMonth:
            theme === null ||
            ['seo', 'sea', 'smo', 'sma', 'days'].includes(theme),
    },
    url: route('front.projectManagement.apikom.postPackages'),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="route('front.projectManagement.apikom.package')"
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
